// Screens and components for the react-app target. Generated by `sui generate` from .sui sources; do not edit.

import { Fragment, useState } from "react";
import {
  ActionButton,
  DataCell,
  DataHeading,
  DataRow,
  DataTable,
  FormBlock,
  FormError,
  Notice,
  NoticeHint,
  OptionItem,
  Page,
  Panel,
  SelectField,
  SidePanel,
  StatusBadge,
  SubmitButton,
  TextField,
} from "./widgets.js";
import { messageOf, useOperations, useNavigate, useQuery } from "./runtime.js";
import type { Action } from "./runtime.js";
import { AccountTypeMembers } from "./types.js";
import type { AccountType } from "./types.js";

export function ChartOfAccountsScreen() {
  const ops = useOperations();
  const navigate = useNavigate();
  const accounts = useQuery(() => ops.listChartAccounts(), [ops]);
  const [drawer, setDrawer] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <Page
      title="Chart of accounts"
      description="Every account this organization posts journal lines against."
      actions={<ActionButton label="New account" onPress={() => setDrawer(true)} />}
    >
      {accounts.error ? (
        <Notice>
          {accounts.error}
          {(accounts.error ?? "").includes("No Console organization context") ? (
            <NoticeHint>
              Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
            </NoticeHint>
          ) : null}
        </Notice>
      ) : null}
      <Panel>
        <TextField
          label="Search accounts"
          id="coa-search"
          bind={{ value: search, set: setSearch }}
          placeholder="Code or name"
          autocomplete="off"
        />
        <DataTable
          head={
            <DataRow>
              <DataHeading label="Code" />
              <DataHeading label="Name" />
              <DataHeading label="Type" />
              <DataHeading label="Status" />
            </DataRow>
          }
        >
          {accounts.data === null ? (
            <DataRow><DataCell span={4}>Loading…</DataCell></DataRow>
          ) : (
            <>
              {(accounts.data?.length ?? 0) === 0 ? (
                <DataRow>
                  <DataCell span={4}>No accounts yet — add the first one with New account.</DataCell>
                </DataRow>
              ) : (
                <>
                  {!accounts.data?.some((item) => String(item["code"] ?? "").includes(search)) && !accounts.data?.some((item) => String(item["name"] ?? "").includes(search)) ? (
                    <DataRow><DataCell span={4}>No accounts match.</DataCell></DataRow>
                  ) : null}
                  {accounts.data?.map((account) => (
                    <Fragment key={account.id}>
                      {account.code.includes(search) || account.name.includes(search) ? (
                        <DataRow onPress={() => navigate(`/chart-of-accounts/${account.id}`)}>
                          <DataCell mono>{account.code}</DataCell>
                          <DataCell>{account.name}</DataCell>
                          <DataCell capitalize>{account.accountType}</DataCell>
                          <DataCell>
                            <StatusBadge tone={account.active ? "success" : "neutral"}>
                              {account.active ? "Active" : "Inactive"}
                            </StatusBadge>
                          </DataCell>
                        </DataRow>
                      ) : null}
                    </Fragment>
                  ))}
                </>
              )}
            </>
          )}
        </DataTable>
      </Panel>
      {drawer ? (
        <NewAccountDrawer onClose={() => setDrawer(false)} onDone={() => accounts.reload()} />
      ) : null}
    </Page>
  );
}

export interface NewAccountDrawerProps {
  readonly onClose: Action;
  readonly onDone: Action;
}

export function NewAccountDrawer({ onClose, onDone }: NewAccountDrawerProps) {
  const ops = useOperations();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("asset");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runAction = async (body: () => Promise<void> | void): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await body();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      title="New account"
      description="Codes group the books (1000s for what you own, 2000s for what you owe, 4000s for income, 6000s for costs)."
      onClose={() => onClose()}
    >
      <FormBlock
        onSubmit={() => runAction(async () => {
          if (code.trim() === "") {
            throw new Error("Code is required (e.g. 1000).");
          }
          if (name.trim() === "") {
            throw new Error("Name is required (e.g. Cash).");
          }
          await ops.createChartAccount({ code: code.trim(), name: name.trim(), accountType });
          await onDone();
          await onClose();
        })}
      >
        {error ? <FormError>{error}</FormError> : null}
        <TextField
          label="Code"
          id="coa-code"
          bind={{ value: code, set: setCode }}
          required
          disabled={busy}
          placeholder="1000"
          autocomplete="off"
          spellcheck={false}
        />
        <TextField
          label="Name"
          id="coa-name"
          bind={{ value: name, set: setName }}
          required
          disabled={busy}
          placeholder="Cash"
          autocomplete="off"
        />
        <SelectField
          label="Type"
          id="coa-type"
          bind={{ value: accountType, set: (next) => setAccountType(next as AccountType) }}
          disabled={busy}
        >
          {AccountTypeMembers.map((kind) => (
            <OptionItem key={kind} value={kind} />
          ))}
        </SelectField>
        <SubmitButton label="Add account" busyLabel="Adding…" busy={busy} />
      </FormBlock>
    </SidePanel>
  );
}
